import type { BrandFeature } from '@eva/schemas';
import { api } from '../api/client';
import type { OnboardingDaMarca } from '../brand-admin/onboarding-da-marca.api';

/** O que o endpoint público de branding devolve e o app usa. */
export interface PublicBrand {
  id: string;
  name: string;
  slug?: string | null;
  /** Domínio personalizado ATIVO da marca (`app.marca.com.br`), se houver (A050). */
  dominio?: string | null;
  branding: {
    logoUrl?: string | null;
    logoHasOwnBackground?: boolean;
    colors?: { primary?: string; secondary?: string; background?: string; text?: string } | null;
    pwaIcons?: { iosIcon?: string | null } | null;
  } | null;
  enabledFeatures: BrandFeature[];
  homeScreenLayout?: unknown;
  contentScreenLayout?: unknown;
  careScreenLayout?: unknown;
  appUiPreferences?: unknown;
  /** Vídeos e campos extras do onboarding (A062, fase C); a marca configura em Configurações › Onboarding. */
  onboarding?: Partial<OnboardingDaMarca> | null;
  /** Cadastro só com link ou convite (A062, fase D): a entrada esconde o "Cadastrar". */
  cadastroSoComLink?: boolean;
}

export const EVA_PUBLIC_BRAND: PublicBrand = {
  id: 'default',
  name: 'Eva',
  slug: null,
  branding: null,
  enabledFeatures: [],
};

/**
 * Layouts das telas da paciente para a Eva: o template global. É o que o
 * mobile usava sem parceiro (`GET /api/public/global-app-config`). O
 * documento `partners/default` pode não existir (emulador, ambiente sem a
 * migração) ou existir sem layouts próprios — nos dois casos a Eva herda o
 * template. Falha aqui não pode impedir a tela de login de aparecer.
 */
async function fetchGlobalLayouts(): Promise<Partial<PublicBrand>> {
  try {
    const g = await api.get<any>('/api/public/global-app-config');
    return {
      homeScreenLayout: g?.homeScreenLayout,
      contentScreenLayout: g?.contentScreenLayout,
      careScreenLayout: g?.careScreenLayout,
      appUiPreferences: g?.appUiPreferences,
    };
  } catch {
    return {};
  }
}

export function urlDoBranding(identifier: string, kind: 'id' | 'slug' | 'host'): string {
  if (kind === 'id') return `/api/public/partners/${identifier}/branding`;
  if (kind === 'host') return `/api/public/partners/by-host/${encodeURIComponent(identifier)}/branding`;
  return `/api/public/partners/slug/${identifier}/branding`;
}

export async function fetchPublicBrand(identifier: string, kind: 'id' | 'slug' | 'host'): Promise<PublicBrand> {
  const url = urlDoBranding(identifier, kind);
  if (identifier !== 'default' || kind !== 'id') return api.get<PublicBrand>(url);

  const [marca, global] = await Promise.all([api.get<PublicBrand>(url).catch(() => null), fetchGlobalLayouts()]);
  const base = marca ?? EVA_PUBLIC_BRAND;
  return {
    ...base,
    homeScreenLayout: base.homeScreenLayout ?? global.homeScreenLayout,
    contentScreenLayout: base.contentScreenLayout ?? global.contentScreenLayout,
    careScreenLayout: base.careScreenLayout ?? global.careScreenLayout,
    appUiPreferences: base.appUiPreferences ?? global.appUiPreferences,
  };
}
